import { FormEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { radius } from "#app/theme/v1/radius";
import { shadows } from "#app/theme/v1/shadows";
import { spacing } from "#app/theme/v1/spacing";
import { useLogin } from "#modules/auth/infra/useLogin";
import { ROUTES } from "#shared/routes";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Paper } from "#shared/view/components/Paper";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";

const VERSION = "0.481.0"; // x-releaser-version

export const LoginPage = () => {
  const [loginForm, setLoginForm] = useState<{
    email: string | null;
    password: string | null;
  }>({
    email: null,
    password: null,
  });

  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromUrl = searchParams.get("from");

  const {
    mutate: loginApi,
    isError,
    isPending,
  } = useLogin({
    onSuccess: () => {
      navigate(fromUrl ?? ROUTES.partner, { replace: true });
    },
  });

  useEffect(() => {
    const accessTokenApi = localStorage.getItem("accessTokenApi");
    if (accessTokenApi && !fromUrl) {
      navigate(ROUTES.partner, { replace: true });
    }
  }, [navigate, fromUrl]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.stopPropagation();
    } else {
      loginApi({
        email: loginForm.email ?? "",
        password: loginForm.password ?? "",
      });
    }
  };

  const setField = (field: string, value: string) => {
    setLoginForm({
      ...loginForm,
      [field]: value,
    });
  };

  return (
    <Paper style={styles.paper}>
      <Box style={styles.loginBox}>
        <Stack gap={"xs"}>
          <Flex align="center" justify="center">
            <img alt="" src="/logo-fabera-login.png" width="139" height="60" />
          </Flex>

          <form onSubmit={handleSubmit}>
            <Flex w="100%" direction={"column"}>
              <TextInput
                style={styles.inputs}
                w={"100%"}
                label={t("login.emailAddress")}
                name="email"
                type="email"
                onChange={(e) => setField("email", e.target.value)}
                error={isError && t("login.incorrectEmailOrPassword")}
                autoComplete="email"
              />
              <TextInput
                style={styles.inputs}
                label={t("login.password")}
                name="password"
                type="password"
                onChange={(e) => setField("password", e.target.value)}
                error={isError && t("login.incorrectEmailOrPassword")}
                autoComplete="password"
              />
              <Flex>
                <Link to={ROUTES.resetPasswordRequest} style={styles.forgotPassword}>
                  <Text>{t("login.forgotPassword")}</Text>
                </Link>
              </Flex>
              <Stack>
                <Button size="lg" loading={isPending} type="submit">
                  {t("login.login")}
                </Button>
              </Stack>
            </Flex>
          </form>
          <Flex justify="center" align="center" direction="column">
            <Text c={colors.gray[5]}>OU</Text>
            <Link to={ROUTES.partnerSignup} style={styles.createAccount}>
              {t("login.createFaberaAccount")}
            </Link>
            <i
              style={{ color: "#d1d1d1", fontSize: 10, marginBottom: 20 }}
            >{`Version: ${VERSION}`}</i>
          </Flex>
        </Stack>
      </Box>
    </Paper>
  );
};

const styles = {
  paper: {
    height: "100vh",
    width: "100wh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--Blue-gradient, linear-gradient(103deg, #2955E2 1.95%, #0B267B 91.53%))",
  },
  loginBox: {
    width: 450,
    padding: spacing.xl,
    borderRadius: radius.md,
    background: "white",
    boxShadow: shadows.md,
  },
  inputs: {
    marginBottom: spacing.xs,
  },
  forgotPassword: {
    color: colors.gray[6],
    marginBottom: spacing.md,
    fontSize: fontSizes.small,
  },
  createAccount: {
    color: colors.gray[6],
    marginTop: spacing.xxxs,
    fontSize: fontSizes.small,
  },
};
